import React, { useCallback, useRef, useState } from 'react'
import { TtIconButton, TtDialog, TtProgressBar } from '@23/react'

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import { ViewportList, type ViewportListRef } from 'react-viewport-list'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'

// @ts-expect-error — importing js
import { VideoSelectorModalComponent } from '../../../sharedLegacy/components'
import { type Speaker } from '../../../sharedLegacy/hooks/useSpeakersQuery'
import { useLocalParticipant } from '../../../webrtc/webRTCRoom'
import {
  useActiveTrayCard,
  addVideoCard,
  updateVideoCard,
  changeCardOrder,
  useTray,
} from '../../../firebase/hooks/useTray'
import { TrayCard } from '../../../firebase/model/tray-cards/cards-model'

import useTrayStore from './useTrayStore'
import useTrayTracking from './useTrayTracking'
import BreakerCard from './BreakerCard'
import NoteCard from './NoteCard'
import VideoCard from './VideoCard'
import ImageCard from './ImageCard'
import ChyronOnCameraCard from './ChyronOnCameraCard/ChyronOnCameraCard'
import ChyronSpeakerCard from './ChyronSpeakerCard'
import ChyronInfoCard from './ChyronInfoCard'
import ComingUpCard from './ComingUpCard'
import ChyronSpeakersOverviewCard from './ChyronSpeakersOverviewCard'
import PollCard, { PollCardNoPoll, PollCardResults } from './PollCard/PollCard'
import HandoutCard from './HandoutCard'
import ScrollProvider from './ScrollProvider'
import GutterMenu from './GutterMenu'

import './Tray.scss'
import useFeatures from '../../../sharedLegacy/hooks/useFeatures'
import { MixedModeTrigger } from './MixedModeTrigger/MixedModeTrigger'
import { TrayFloorTrigger } from './TrayFloorTrigger'

interface TrayProps {
  speakers: Speaker[]
}

const Tray = ({ speakers }: TrayProps): JSX.Element => {
  const [isSorting, setIsSorting] = useState(false)

  const ref = useRef<HTMLDivElement | null>(null)
  const cardListRef = useRef<ViewportListRef>(null)

  const [isTrayOpen, setIsTrayOpen] = useState(true)

  const { cards } = useTray()
  const activeTrayCard = useActiveTrayCard()

  const handoutUpload = useTrayStore((state) => state.handoutUpload)

  const sensors = useSensors(useSensor(PointerSensor))
  const trayTracking = useTrayTracking()

  const intention = useTrayStore((state) => state.intention)
  const clearIntention = useTrayStore((state) => state.clearIntention)

  const localParticipant = useLocalParticipant()

  const isActiveCard = useCallback(
    (id: string) => id === activeTrayCard,
    [activeTrayCard]
  )

  const { HasPanelMixedModeEnabled } = useFeatures()

  const isPriorToSelectedCard = useCallback(
    (id: string) => {
      if (!cards || cards.length === 0) return false

      if (activeTrayCard) {
        const currentIndex = cards.findIndex((c) => c.id === id)
        const activeIndex = cards.findIndex((c) => c.id === activeTrayCard)

        if (currentIndex === -1 || activeIndex === -1) {
          return false
        }

        if (currentIndex < activeIndex) {
          return true
        }
      }

      return false
    },
    [cards, activeTrayCard]
  )

  const isLastCard = useCallback(
    (cardId: string) => {
      if (!cards || cards.length === 0) {
        return false
      }

      const lastCard = cards[cards.length - 1]
      return lastCard.id === cardId
    },
    [cards]
  )

  const onTrayCloseClickHandler = useCallback(() => {
    setIsTrayOpen(false)
  }, [])

  const onTrayOpenClickHandler = useCallback(() => {
    setIsTrayOpen(true)
  }, [])

  const isModalOpen =
    intention?.type === 'video' &&
    (intention?.action === 'add' || intention?.action === 'edit')

  const shownSpeakers = speakers.filter(
    (s) => s.creationSource !== 'guest' && s.isShowingTitleCard
  )

  const onDragEndHandler = async (event: DragEndEvent) => {
    setIsSorting(false)

    const { active, over } = event

    if (
      over &&
      active.id &&
      typeof active.id === 'string' &&
      typeof over.id === 'string' &&
      active.id !== over.id
    ) {
      await changeCardOrder(active.id, over.id)
    }
  }

  const onDragStartHandler = async (_event: DragStartEvent) => {
    setIsSorting(true)
  }

  // Transform playback url to use the best quality available
  const transformPlaybackUrl = (playback?: string) => {
    return playback?.replace('/video_medium/', '/video_1080p/')
  }

  return (
    <>
      <ScrollProvider listRef={cardListRef}>
        <div className="tray" data-open={isTrayOpen}>
          <div className="tray-header">
            {isTrayOpen ? (
              <TtIconButton
                library="one/webinars"
                name="tray-open"
                label="close webinar tray"
                onClick={() => {
                  trayTracking.closed(isTrayOpen)
                  onTrayCloseClickHandler()
                }}
              />
            ) : (
              <TtIconButton
                library="one/webinars"
                name="tray-closed"
                label="open webinar tray"
                onClick={() => {
                  trayTracking.closed(isTrayOpen)
                  onTrayOpenClickHandler()
                }}
              />
            )}
            <div className="tray-header-title">WebinarTray</div>
            {HasPanelMixedModeEnabled ? (
              <TrayFloorTrigger />
            ) : (
              <MixedModeTrigger />
            )}
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEndHandler}
            onDragStart={onDragStartHandler}
          >
            <div
              className="tray-list"
              ref={ref}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                overflowX: 'hidden',
                scrollBehavior: isSorting ? 'auto' : 'smooth',
              }}
            >
              {(!cards || cards.length === 0) && <GutterMenu isStandalone />}

              <SortableContext
                items={(cards as TrayCard[]) ?? []}
                strategy={verticalListSortingStrategy}
              >
                <ViewportList
                  ref={cardListRef}
                  viewportRef={ref}
                  items={(cards as TrayCard[]) ?? []}
                  axis="y"
                  overscan={30}
                  scrollThreshold={1}
                  withCache
                >
                  {(card, index) => {
                    const isActiveCardValue = isActiveCard(card.id)
                    const isPriorToSelectedCardValue: boolean =
                      isPriorToSelectedCard(card.id)
                    const isLastCardValue = isLastCard(card.id)

                    switch (card.type) {
                      case 'breaker':
                        return (
                          <BreakerCard
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'note':
                        return (
                          <NoteCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'video':
                        return (
                          <VideoCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'image':
                        return localParticipant ? (
                          <ImageCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            participantId={localParticipant?.id}
                            key={card.id}
                            card={card}
                          />
                        ) : null
                      case 'handout':
                        return (
                          <HandoutCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'chyron-speaker':
                        return (
                          <ChyronSpeakerCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                            speakers={shownSpeakers}
                          />
                        )
                      case 'chyron-speakers-overview':
                        return (
                          <ChyronSpeakersOverviewCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                            speakers={shownSpeakers}
                          />
                        )
                      case 'chyron-info':
                        return (
                          <ChyronInfoCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'chyron-on-camera':
                        return (
                          <ChyronOnCameraCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'coming-up':
                        return (
                          <ComingUpCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          />
                        )
                      case 'poll':
                        return (
                          <PollCard
                            isActiveCard={isActiveCardValue}
                            isPriorToSelectedCard={isPriorToSelectedCardValue}
                            isLastCard={isLastCardValue}
                            position={index}
                            key={card.id}
                            card={card}
                          >
                            {card.poll ? (
                              <PollCardResults poll={card.poll} />
                            ) : (
                              <PollCardNoPoll />
                            )}
                          </PollCard>
                        )
                      default:
                        return <div />
                    }
                  }}
                </ViewportList>
                {/* </Droppable> */}
              </SortableContext>
            </div>
          </DndContext>
        </div>
      </ScrollProvider>
      <VideoSelectorModalComponent
        onChange={(
          videos: Array<{
            photo_id: string
            title: string
            medium_download: string
            video_1080p_download: string
            video_hd_download: string
            video_medium_download: string
          }>
        ) => {
          const video = videos[0]
          if (video && intention && intention.type === 'video') {
            const { currentCardId } = intention
            const { title: videoTitle } = video
            const videoId = video.photo_id
            const thumbnail = video.medium_download
            const playback = transformPlaybackUrl(video.video_medium_download)

            if (playback) {
              if (intention.action === 'add') {
                const card = addVideoCard(
                  videoId,
                  videoTitle,
                  thumbnail,
                  playback,
                  currentCardId
                )
                trayTracking.created(card.type, currentCardId)
              }
              if (intention.action === 'edit' && currentCardId) {
                updateVideoCard(
                  currentCardId,
                  videoId,
                  videoTitle,
                  thumbnail,
                  playback
                )
              }
            }
          }
          clearIntention()
        }}
        value={[]}
        selectorProps={{
          searchIn: ['photos'],
          theme: 'dark',
          selectionLimit: 1,
        }}
        isModalOpen={isModalOpen}
        onCloseModal={clearIntention}
        isResettingStateOnClose
      />
      <TtDialog
        open={handoutUpload.inProgress}
        label="Uploading"
        closable={false}
        className="upload-dialog"
      >
        <TtProgressBar indeterminate />
      </TtDialog>
    </>
  )
}

export default Tray
